<template>
    <div class="fence-map">
        <div ref="map" class="fence-map__canvas" :style="{ height: height }"></div>
        <div class="fence-map__legend caption">
            <span v-if="company"><span class="fence-map__dot fence-map__dot--company"></span> Empresa</span>
            <span v-if="position"><span class="fence-map__dot fence-map__dot--position"></span> Você</span>
            <span v-if="company && radius"><span class="fence-map__dot fence-map__dot--fence" :class="`fence-map__dot--${status}`"></span> Raio de {{ radius }} m</span>
        </div>
    </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const FENCE_COLORS = {
    ok: '#2e7d32',       // dentro do raio
    error: '#c62828',    // fora do raio
    warning: '#ef6c00',  // leitura imprecisa
    neutral: '#1565c0',  // sem validação
}

const MAX_ACCURACY_DRAW_METERS = 5000 // acima disso o círculo de erro não ajuda e distorce o zoom

export default {
    name: 'SysOccurrenceFenceMap',
    props: {
        // { lat, lng } da empresa (centro da cerca eletrônica)
        company: { type: Object, default: null },
        // { lat, lng, accuracy } da leitura atual do professor
        position: { type: Object, default: null },
        // raio da cerca eletrônica, em metros (nulo = sem cerca)
        radius: { type: Number, default: null },
        // ok | error | warning | neutral
        status: { type: String, default: 'neutral' },
        height: { type: String, default: '220px' },
    },
    data: () => ({
        map: null,
        layers: null,
        resizeTimer: null,
    }),
    mounted() {
        this.map = L.map(this.$refs.map, { zoomControl: true, attributionControl: true });

        // Visão inicial (Brasil): o Leaflet só adiciona as camadas depois que o mapa tem centro e zoom.
        this.map.setView([-14.235, -51.9253], 4);

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
        }).addTo(this.map);

        this.layers = L.layerGroup().addTo(this.map);
        this.draw();

        // O mapa pode nascer dentro de um modal ainda animando (largura 0): recalcula o tamanho.
        this.resizeTimer = setTimeout(() => {
            if (this.map) {
                this.map.invalidateSize();
                this.draw();
            }
        }, 350);
    },
    beforeDestroy() {
        clearTimeout(this.resizeTimer);

        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },
    watch: {
        company: { deep: true, handler() { this.draw(); } },
        position: { deep: true, handler() { this.draw(); } },
        radius() { this.draw(); },
        status() { this.draw(); },
    },
    methods: {
        draw() {
            if (!this.map || !this.layers) {
                return;
            }

            this.layers.clearLayers();

            const bounds = [];
            const color = FENCE_COLORS[this.status] || FENCE_COLORS.neutral;

            if (this.company) {
                const center = [this.company.lat, this.company.lng];

                if (this.radius) {
                    L.circle(center, {
                        radius: this.radius,
                        color,
                        weight: 2,
                        fillColor: color,
                        fillOpacity: 0.12,
                    }).addTo(this.layers);

                    // Limites calculados em metros (sem depender das camadas já estarem no mapa)
                    bounds.push(L.latLng(center).toBounds(this.radius * 2));
                }

                L.circleMarker(center, {
                    radius: 8,
                    color: '#fff',
                    weight: 2,
                    fillColor: '#6a1b9a',
                    fillOpacity: 1,
                }).bindTooltip('Empresa').addTo(this.layers);

                bounds.push(L.latLngBounds([center, center]));
            }

            if (this.position) {
                const point = [this.position.lat, this.position.lng];
                const accuracy = Number(this.position.accuracy);

                if (accuracy > 0 && accuracy <= MAX_ACCURACY_DRAW_METERS) {
                    L.circle(point, {
                        radius: accuracy,
                        color: '#1565c0',
                        weight: 1,
                        dashArray: '4',
                        fillColor: '#1565c0',
                        fillOpacity: 0.1,
                    }).addTo(this.layers);

                    bounds.push(L.latLng(point).toBounds(accuracy * 2));
                }

                L.circleMarker(point, {
                    radius: 7,
                    color: '#fff',
                    weight: 2,
                    fillColor: '#1565c0',
                    fillOpacity: 1,
                }).bindTooltip('Você está aqui').addTo(this.layers);

                bounds.push(L.latLngBounds([point, point]));
            }

            if (!bounds.length) {
                return;
            }

            const all = bounds.reduce((acc, b) => acc.extend(b), L.latLngBounds(bounds[0].getSouthWest(), bounds[0].getNorthEast()));

            this.map.fitBounds(all, { padding: [24, 24], maxZoom: 18, animate: false });
        },
    },
}
</script>

<style scoped>
.fence-map__canvas {
    width: 100%;
    border-radius: 4px;
    z-index: 0;
}

.fence-map__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 4px;
}

.fence-map__dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;
}

.fence-map__dot--company { background: #6a1b9a; }
.fence-map__dot--position { background: #1565c0; }
.fence-map__dot--fence { background: transparent; border: 2px solid #1565c0; }
.fence-map__dot--ok { border-color: #2e7d32; }
.fence-map__dot--error { border-color: #c62828; }
.fence-map__dot--warning { border-color: #ef6c00; }
</style>
